import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

export type OrgSwitchPhase = 'auth' | 'data' | null

export type OrgSwitchHandlers = {
  beginSwitch: (orgId: string, label?: string) => void
  enterDataPhase: (orgId: string) => void
  failSwitch: () => void
  endSwitch: () => void
}

export type OrgSwitchContextValue = OrgSwitchHandlers & {
  isSwitching: boolean
  phase: OrgSwitchPhase
  targetOrgId: string | null
  targetLabel: string | null
  /** Monotonic id — coordinator ignores stale generations after rapid re-switch. */
  switchGeneration: number
}

const OrgSwitchContext = createContext<OrgSwitchContextValue | null>(null)

export function OrgSwitchProvider({ children }: { children: ReactNode }) {
  const [isSwitching, setIsSwitching] = useState(false)
  const [phase, setPhase] = useState<OrgSwitchPhase>(null)
  const [targetOrgId, setTargetOrgId] = useState<string | null>(null)
  const [targetLabel, setTargetLabel] = useState<string | null>(null)
  const switchGenerationRef = useRef(0)
  const [switchGeneration, setSwitchGeneration] = useState(0)

  const reset = useCallback(() => {
    setIsSwitching(false)
    setPhase(null)
    setTargetOrgId(null)
    setTargetLabel(null)
  }, [])

  const beginSwitch = useCallback((orgId: string, label?: string) => {
    switchGenerationRef.current += 1
    setSwitchGeneration(switchGenerationRef.current)
    setIsSwitching(true)
    setPhase('auth')
    setTargetOrgId(orgId)
    setTargetLabel(label ?? null)
  }, [])

  const enterDataPhase = useCallback((orgId: string) => {
    setTargetOrgId(orgId)
    setPhase('data')
  }, [])

  const endSwitch = useCallback(() => {
    reset()
  }, [reset])

  const failSwitch = useCallback(() => {
    reset()
  }, [reset])

  const value = useMemo<OrgSwitchContextValue>(
    () => ({
      isSwitching,
      phase,
      targetOrgId,
      targetLabel,
      switchGeneration,
      beginSwitch,
      enterDataPhase,
      endSwitch,
      failSwitch
    }),
    [isSwitching, phase, targetOrgId, targetLabel, switchGeneration, beginSwitch, enterDataPhase, endSwitch, failSwitch]
  )

  return <OrgSwitchContext.Provider value={value}>{children}</OrgSwitchContext.Provider>
}

export function useOrgSwitch(): OrgSwitchContextValue {
  const ctx = useContext(OrgSwitchContext)
  if (!ctx) {
    throw new Error('useOrgSwitch must be used within OrgSwitchProvider')
  }
  return ctx
}

/** Handlers subset for passing into `useSetActiveOrganization` from apiLayer. */
export function useOrgSwitchHandlers(): OrgSwitchHandlers {
  const { beginSwitch, enterDataPhase, failSwitch, endSwitch } = useOrgSwitch()
  return useMemo(
    () => ({ beginSwitch, enterDataPhase, failSwitch, endSwitch }),
    [beginSwitch, enterDataPhase, failSwitch, endSwitch]
  )
}
